import React, { useEffect, useState } from "react";
import { Typography, Progress, Statistic, Skeleton, Button, message, Popconfirm } from "antd";
import { useHistory } from "react-router-dom";
import { isMobileOnly } from "react-device-detect";
import { ExperimentOutlined } from "@ant-design/icons";
import "./WelcomeCard.css";
import CardWizz from "../../../blocks/Card/Card";
import {
  ApplicationList,
  populateTestProfile,
} from "../../../services/applications.services";

const { Title, Paragraph, Text } = Typography;

const WelcomeCard = ({
  vendorId,
  progressPercent,
  userDetails,
  isSubscribed,
  needsRecertification,
}) => {
  const history = useHistory();
  const [loadingTestData, setLoadingTestData] = useState(false);
  // Cached application id for the active recert cycle. Used so the card click
  // can deep-link straight to the PQQ gate instead of routing through
  // /get-certified, which used to fail because GetCertified.handlePayment
  // wasn't passing the application_id the backend requires.
  const [recertApplicationId, setRecertApplicationId] = useState(null);
  const isDevelopment = process.env.NODE_ENV === 'development' ||
                        window.location.hostname === 'localhost';

  useEffect(() => {
    if (!needsRecertification || !vendorId) {
      setRecertApplicationId(null);
      return;
    }
    let cancelled = false;
    ApplicationList(vendorId)
      .then((response) => {
        if (cancelled) return;
        const apps = response?.data?.applications || [];
        const app = apps.find((a) => a.recertify) || apps[0];
        setRecertApplicationId(app?.application_id || null);
      })
      .catch(() => {
        if (cancelled) return;
        setRecertApplicationId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [needsRecertification, vendorId]);

  const redirectToVendorProfile = () => {
    if (needsRecertification) {
      const target = recertApplicationId
        ? `/vendor/${vendorId}/PQQ?form=${recertApplicationId}`
        : `/vendor/${vendorId}/PQQ`;
      !isMobileOnly && history.push(target);
      return;
    }
    if (progressPercent < 100 || (progressPercent === 100 && isSubscribed)) {
      !isMobileOnly &&
        history.push(`/vendor/${vendorId}/vendor-profile/basic-details`);
    } else if (progressPercent === 100) {
      !isMobileOnly && history.push(`/vendor/${vendorId}/get-certified`);
    }
  };

  const handlePopulateTestData = () => {
    setLoadingTestData(true);
    populateTestProfile(vendorId)
      .then((response) => {
        message.success(
          `Test profile populated! Completion: ${response.data.profile_completion}%`
        );
        // Reload the page to refresh vendor details
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      })
      .catch((error) => {
        console.error("populate test profile error", error);
        if (error && error.response) {
          message.error(
            error.response.data.message || error.response.message || "Failed to populate test data"
          );
        } else {
          message.error("Failed to populate test data");
        }
      })
      .finally(() => {
        setLoadingTestData(false);
      });
  };

  return (
    <CardWizz className="welcome-card" onClick={redirectToVendorProfile}>
      {progressPercent !== undefined ? (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Title level={3} style={{ marginBottom: "12px", marginTop: 0 }}>Hello {userDetails?.first_name}</Title>
          <Paragraph style={{ fontSize: "12px", marginBottom: "16px", flex: 1 }}>
            {needsRecertification
              ? `Annual recertification is required. Review your i4Profile for any changes, then open your PQQ to confirm, pay the renewal fee ($750.00 + 2.9% transaction fee), and submit.`
              : progressPercent < 100
              ? `Welcome to the Vendor Pre-Qualification process! Please click here to complete your vendor profile.`
              : isSubscribed
              ? `Great! You've completed your i4Profile. `
              : `Great! you've completed your i4Profile. Get subscription by clicking here.`}
          </Paragraph>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
            <div>
              {isDevelopment && progressPercent < 100 && (
                <Popconfirm
                  title="Populate Test Data"
                  description="This will fill your vendor profile with test data so you can skip to Step 11. Continue?"
                  onConfirm={(e) => {
                    e.stopPropagation();
                    handlePopulateTestData();
                  }}
                  okText="Yes"
                  cancelText="No"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    type="dashed"
                    size="small"
                    icon={<ExperimentOutlined />}
                    loading={loadingTestData}
                    style={{ fontSize: '11px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Test Mode: Fill Profile
                  </Button>
                </Popconfirm>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
              <Text style={{ fontSize: "13px", color: "#7f7f7f" }}>Profile Completion</Text>
              <Progress
                width={75}
                type="dashboard"
                strokeWidth={12}
                percent={(progressPercent && progressPercent) || 0}
                format={(percent) => <Statistic value={percent} suffix="%" />}
                strokeColor={{
                  "0%": "#108ee9",
                  "100%": "#87d068",
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <Skeleton active size="small" />
      )}
    </CardWizz>
  );
};

export default WelcomeCard;
